<template>
  <d2-container>
    <el-row :gutter="24">
      <el-col :xs="24" :md="14">
        <el-card shadow="never">
          <div slot="header" class="card-header">
            <span>小程序码参数</span>
            <el-button type="text" @click="resetForm">恢复默认</el-button>
          </div>
          <el-form ref="form" :model="form" :rules="rules" label-width="120px" size="small">
            <el-form-item label="小程序端" prop="userType">
              <el-radio-group v-model="form.userType">
                <el-radio :label="0">用户端</el-radio>
                <el-radio :label="1">商家端</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="版本" prop="envVersion">
              <el-select v-model="form.envVersion" clearable style="width: 240px">
                <el-option label="跟随系统配置" value="" />
                <el-option label="正式版（release）" value="release" />
                <el-option label="体验版（trial）" value="trial" />
                <el-option label="开发版（develop）" value="develop" />
              </el-select>
              <span class="field-tip">不传时使用系统配置的 wechat.envVersion</span>
            </el-form-item>
            <el-form-item label="页面路径" prop="page">
              <el-input v-model.trim="form.page" placeholder="例如 pages/intimate/intimate" />
              <span class="field-tip">不要填写开头的“/”；密语卡通常使用 pages/intimate/intimate</span>
            </el-form-item>
            <el-form-item label="页面宽度" prop="width">
              <el-input-number v-model="form.width" :min="280" :max="1280" :step="10" controls-position="right" />
              <span class="field-tip">单位 px，范围 280–1280</span>
            </el-form-item>
            <el-form-item label="透明底色">
              <el-switch v-model="form.isHyaline" active-text="是" inactive-text="否" />
            </el-form-item>
            <el-form-item label="自动线条颜色">
              <el-switch v-model="form.autoColor" active-text="是" inactive-text="否" />
            </el-form-item>
            <el-form-item v-if="!form.autoColor" label="线条颜色">
              <el-input-number v-model="form.lineColor.r" :min="0" :max="255" controls-position="right" />
              <el-input-number v-model="form.lineColor.g" :min="0" :max="255" controls-position="right" />
              <el-input-number v-model="form.lineColor.b" :min="0" :max="255" controls-position="right" />
              <span class="field-tip">RGB（0–255）</span>
            </el-form-item>

            <el-divider content-position="left">场景参数（可选）</el-divider>
            <el-form-item label="跳转类型">
              <el-input-number v-model="form.skipType" :min="0" :step="1" controls-position="right" placeholder="skipType" />
            </el-form-item>
            <el-form-item label="操作类型">
              <el-radio-group v-model="form.opType">
                <el-radio :label="0">普通</el-radio>
                <el-radio :label="1">赠送</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="操作值">
              <el-input-number v-model="form.value" :min="0" :step="1" controls-position="right" placeholder="可填写订单、商品等 ID" />
            </el-form-item>
            <el-form-item label="跳转链接">
              <el-input v-model.trim="form.url" placeholder="例如 pages/secretLanguage/receiveGifts/receiveGifts?arMsgOrderId=订单ID" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-picture" :loading="loading" @click="generate">生成小程序码</el-button>
              <el-button v-if="qrCodeUrl" icon="el-icon-download" @click="download">下载图片</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="10">
        <el-card shadow="never" class="preview-card">
          <div slot="header">预览</div>
          <div v-if="qrCodeUrl" class="qr-preview">
            <el-image :src="qrCodeUrl" fit="contain" :preview-src-list="[qrCodeUrl]" />
            <div class="preview-tip">点击图片可放大查看</div>
          </div>
          <el-empty v-else description="填写参数后生成小程序码" :image-size="120" />
        </el-card>
        <el-alert
          class="help-alert"
          title="说明"
          type="info"
          :closable="false"
          show-icon
          description="二维码场景值由服务端生成并保存，页面、版本和端类型按本页参数传递。envVersion 留空时由后端使用 wechat.envVersion。"
        />
      </el-col>
    </el-row>
  </d2-container>
</template>

<script>
import { generateMiniProgramQrCode } from '@/api/demo/miniProgramQrCode'

const defaultForm = () => ({
  userType: 0,
  envVersion: '',
  page: 'pages/intimate/intimate',
  width: 430,
  isHyaline: false,
  autoColor: true,
  lineColor: { r: 0, g: 0, b: 0 },
  skipType: null,
  opType: 0,
  value: null,
  url: ''
})

export default {
  name: 'miniProgramQrCode',
  data () {
    return {
      form: defaultForm(),
      loading: false,
      qrCodeUrl: '',
      qrCodeBlob: null,
      rules: {
        page: [
          { required: true, message: '请输入页面路径', trigger: 'blur' },
          { max: 1024, message: '页面路径不能超过 1024 个字符', trigger: 'blur' }
        ],
        width: [
          { required: true, message: '请输入页面宽度', trigger: 'change' }
        ]
      }
    }
  },
  created () {
    // 从订单列表跳转时可通过 ?orderId=... 直接填入密语卡接收页。
    const orderId = this.$route && this.$route.query && this.$route.query.orderId
    if (orderId && /^\d+$/.test(String(orderId))) {
      this.form.url = 'pages/secretLanguage/receiveGifts/receiveGifts?arMsgOrderId=' + orderId
    }
  },
  beforeDestroy () {
    this.revokeQrCodeUrl()
  },
  methods: {
    revokeQrCodeUrl () {
      if (this.qrCodeUrl) {
        window.URL.revokeObjectURL(this.qrCodeUrl)
      }
      this.qrCodeUrl = ''
      this.qrCodeBlob = null
    },
    resetForm () {
      this.revokeQrCodeUrl()
      this.form = defaultForm()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    buildPayload () {
      const payload = {
        userType: this.form.userType,
        page: (this.form.page || '').replace(/^\/+/, ''),
        width: this.form.width,
        is_hyaline: this.form.isHyaline,
        auto_color: this.form.autoColor
      }
      if (this.form.envVersion) payload.envVersion = this.form.envVersion
      if (!this.form.autoColor) payload.line_color = { ...this.form.lineColor }
      if (this.form.skipType !== null && this.form.skipType !== '') payload.skipType = this.form.skipType
      if (this.form.opType !== null && this.form.opType !== '') payload.opType = this.form.opType
      if (this.form.value !== null && this.form.value !== '') payload.value = this.form.value
      if (this.form.url) payload.url = this.form.url
      return payload
    },
    generate () {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.loading = true
        generateMiniProgramQrCode(this.buildPayload()).then(response => {
          const blob = response && response.data instanceof Blob ? response.data : response
          if (!(blob instanceof Blob) || blob.size === 0) {
            throw new Error('二维码返回内容为空')
          }
          this.revokeQrCodeUrl()
          this.qrCodeBlob = blob
          this.qrCodeUrl = window.URL.createObjectURL(blob)
          this.$message.success('小程序码生成成功')
        }).catch(error => {
          // 后端错误通常也会以 blob 返回，这里只展示统一提示，避免把二进制内容直接渲染出来。
          console.error(error)
          this.$message.error('小程序码生成失败，请检查参数或小程序配置')
        }).finally(() => {
          this.loading = false
        })
      })
    },
    download () {
      if (!this.qrCodeBlob) return
      const link = document.createElement('a')
      link.href = this.qrCodeUrl
      link.download = 'mini-program-qrcode.jpg'
      link.click()
    }
  }
}
</script>

<style lang="scss" scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.field-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}
.preview-card {
  min-height: 430px;
}
.qr-preview {
  text-align: center;
  .el-image {
    width: 320px;
    height: 320px;
  }
}
.preview-tip {
  color: #909399;
  font-size: 12px;
  margin-top: 8px;
}
.help-alert {
  margin-top: 16px;
}
</style>
